import React from 'react';
import delve from 'dlv';

import DoubleMetricCell from 'components/data-visualization/Table/DoubleMetricCell';

import { entityTypeToApiType } from 'helpers/mapEntityTypes';
import { ITableRowParameters, IBrowseTableRowProps } from '../shared/types';
import { IMetric } from 'maps/metrics';

import {
  TableRow,
  EntityCell,
  CellContent,
  StyledEntityItem,
} from './TableRow.style';

import BrowseTableCell from '../BrowseTableCell/BrowseTableCell';

/**
 * BrowseTableRow is a single table row displaying multiple metrics
 * per cell.
 *
 * @param {Object} props.entity the current entity (e.g track) to be displayed
 * @param {String} props.browseEntityType the type of the entity (e.g artist)
 * @param {String} props.tableName="BrowseOpus" temporary param
 * @param {Object} props.sortBy the metric the table is currently sorted by
 * @param {Boolean} props.isLargeScreen true if we are on a large screen
 * @param {Boolean} props.isHighlighted is the current row the same as the entity page?
 * @param {Array} props.tableHeaders array of objects that make up the table headers
 *
 */

const BrowseTableRow: React.FC<IBrowseTableRowProps> = (props) => {
  const {
    entity,
    browseEntityType,
    sortBy,
    isLargeScreen,
    isHighlighted,
    tableHeaders,
    tableName,
    newDesign,
    releaseCompilation,
    isTrackCountDisplayed,
  } = props;

  const entityInfo = delve(
    entity,
    tableName === 'customLists' || tableName === 'trackCustomLists'
      ? 'entity'
      : (entityTypeToApiType(browseEntityType) as string)
  );
  const entityData = browseEntityType === 'label' ? entityInfo : entity.data;

  // Display name is not a data cell we want to loop over.
  const filterDisplayName = (
    header: { label: string; value: string } | IMetric
  ): boolean => header.value !== 'displayName';

  // On large screens, filter nothing, otherwise only return
  // the active metric as mobile is single column.
  const filterByBreakpoint = (header: ITableRowParameters): boolean =>
    isLargeScreen || header.key === sortBy.key || header.change === sortBy.key;

  const allDataCellsOrOnlyActive = tableHeaders
    .filter(filterDisplayName)
    .filter(filterByBreakpoint);

  return (
    <TableRow>
      <EntityCell twoCols={tableHeaders.length === 2}>
        <CellContent>
          <StyledEntityItem
            entityId={entityInfo.id}
            entityType={entityInfo.type}
            displayName={entityInfo.displayName}
            primaryArtist={entityInfo.primaryArtist}
            thumbnailUrl={entityInfo.thumbnailUrl}
            releaseDate={entityInfo.releaseDate}
            isGroup={entityInfo.is_group}
            parentEntityId={entityInfo.parent_group_id}
            groupChildCount={entityInfo.group_child_count}
            entityData={entityData}
            releaseCount={entityInfo.grouped_album_count}
            artistCount={entityInfo.artist_count}
            trackCount={entityInfo.grouped_track_count}
            preserveSearch={true}
            isHighlighted={isHighlighted}
            isLink={true}
            releaseType={entity?.album?.productType}
            isrc_list={entityInfo.isrc_list}
            releaseCompilation={releaseCompilation}
            isTrackCountDisplayed={isTrackCountDisplayed}
          />
        </CellContent>
      </EntityCell>
      {/* The labels table already has all the data in the EntityItem above */}
      {browseEntityType === 'label' && !isLargeScreen
        ? null
        : allDataCellsOrOnlyActive.map(
            (header: ITableRowParameters): JSX.Element => {
              return tableName &&
                (tableName === 'BrowseOpus' ||
                  tableName === 'customLists' ||
                  tableName === 'trackCustomLists' ||
                  tableName === 'TopLists' ||
                  tableName.match(/Browse Opus/)) ? (
                <BrowseTableCell
                  key={Object.values(header).join('-')}
                  header={header}
                  entityData={entityData}
                  newDesign={newDesign}
                />
              ) : (
                <DoubleMetricCell
                  key={Object.values(header).join('-')}
                  sortBy={sortBy}
                  header={header}
                  browseEntityType={browseEntityType}
                  entityData={entityData}
                  tableName={tableName}
                />
              );
            }
          )}
    </TableRow>
  );
};

export default BrowseTableRow;
